import { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Box, Button, ButtonLink, Card, Heading, Stack, Text } from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { OrderManagementWithItems } from '@sitecore/types/manual/be/OrderManagementWithItems';
import { useRouter } from 'next/router';

import { LoadingListSpinner } from '../../../../components/Loading/LoadingListSpinner';
import { buildNavigationHref } from '../../../../utils/buildNavigationHref';
import { SelectedProduct, SelectedServices } from '../../components/SelectedProductCards';
import { useOrderId } from '../../hooks/useOrderId';
import { useSelectedProducts } from '../../hooks/useSelectedProducts';
import { FlowChildProps } from '../../types/OrderManagementSteps';

export const SelectedProductsOverview: FC<FlowChildProps> = ({ nextMainStep }) => {
  const { fields } = useContent<OrderManagementWithItems>();
  const { query } = useRouter();
  const orderId = useOrderId();
  const priceSimulationId = query.priceSimulationId as string | undefined;

  const { hasSelectedProducts, hasSelectedServices, isLoading } = useSelectedProducts({
    orderId,
  });

  const onNextClick = () => {
    nextMainStep();
  };

  return (
    <>
      <Stack direction="column" gap="2">
        <Heading as="h1" size="M">
          {fields.selectedProductsStep.title.value}
        </Heading>
        <Text size="BodyM">
          <RichText html={fields.selectedProductsStep.subTitle.value} />
        </Text>
      </Stack>
      {isLoading ? (
        <LoadingListSpinner />
      ) : (
        <>
          {hasSelectedProducts || hasSelectedServices ? (
            <>
              <SelectedProduct />
              <SelectedServices />
              <Button onClick={onNextClick}>{fields.navigation.nextButtonLabel.value}</Button>
            </>
          ) : (
            <>
              <Card>
                <Box padding="6">
                  <Text size="BodyM" weight="bold">
                    <RichText html={fields.selectedProductsStep.emptyStateContent.value} />
                  </Text>
                </Box>
              </Card>
              <ButtonLink
                href={buildNavigationHref(fields.navigation.priceSimulationLink.value.href, {
                  queryParams: {
                    priceSimulationId,
                    orderId,
                  },
                })}
                target={fields.navigation.priceSimulationLink.value.target}>
                {fields.selectedProductsStep.emptyStateLink.value.text}
              </ButtonLink>
            </>
          )}
          <Text size="BodyS" color="textLowEmphasis">
            <RichText html={fields.selectedProductsStep.description.value} />
          </Text>
        </>
      )}
    </>
  );
};
